import { observable, action, computed } from "mobx";
import { debounce, findIndex } from "lodash";
import Api from "~/service/Api";
import PhotoEditingJob from "~/models/PhotoEditing/PhotoEditingJob";
import JobStatus from "~/constants/PhotoEditing/JobStatus";
import Product from "~/models/Product";
import JobType from "~/constants/PhotoEditing/JobType";
import ProductIdPerJobType from "~/constants/PhotoEditing/ProductIdPerJobType";
import HdRealImagesPerProduct from "~/constants/PhotoEditing/HdRealImagesPerProduct";

class ReviewJobStore {
  /**
   * @property {boolean}
   */
  @observable isLoading = false;

  /**
   * @property {boolean}
   */
  @observable isSavingLabel = false;

  /**
   * @property {boolean}
   */
  @observable isSavingImage = false;

  /**
   * @property {string}
   */
  @observable reviewImagesUrl = "";

  /**
   * @property {string}
   */
  @observable label = "";

  /**
   * @property {Object|null}
   */
  @observable photoEditingJob = null;

  /**
   * @property {string}
   */
  @observable redirectTo = "";

  /**
   * @property {number|null}
   */
  photoEditingJobId = null;

  /**
   * @property {Object}
   */
  @observable photoEditingImageProducts = {};

  /**
   * @property {Product|null}
   */
  @observable photoEditingImageProduct = null;

  constructor(photoEditingJobId) {
    this.photoEditingJobId = photoEditingJobId;
    // path relative to base path set in Routes.jsx
    this.reviewImagesUrl = `/${photoEditingJobId}/images`;
    this.photoEditingJob = new PhotoEditingJob({});
    this.photoEditingImageProduct = new Product({});

    // Saving an image will  trigger on click event like when changing day time (day / night) or
    // location (interior / exterior) so we need to prevent the user from triggering too many
    // requests. The "leading: true" option is important because the functions return a promise.
    this.saveImage = debounce(this.saveImage.bind(this), 500, { leading: true });
    this.batchUpdateImageSettings = debounce(this.batchUpdateImageSettings.bind(this), 500, { leading: true });
  }

  @action
  load() {
    this.isLoading = true;

    return Promise.all([
      this.getPhotoEditingJob(),
      this.updatePhotoEditingJobEstimatedTime(),
      this.getPhotoEditingImageProduct(JobType.HD_REAL, ProductIdPerJobType[JobType.HD_REAL]),
      this.getPhotoEditingImageProduct(JobType.BASIC_HDR, ProductIdPerJobType[JobType.BASIC_HDR]),
    ]).then(() => {
      this.photoEditingImageProduct = this.photoEditingImageProducts[this.photoEditingJob.typeId];
      this.isLoading = false;
    });
  }

  @action
  getPhotoEditingJob() {
    return Api.get(`/photo-editing/jobs/${this.photoEditingJobId}`).then(response => {
      this.photoEditingJob = new PhotoEditingJob(response.data.photoEditingJob);

      // Redirect to view job if the job is not in the process of creation any more
      if (this.photoEditingJob.status !== JobStatus.IN_CREATION) {
        this.redirectTo = `/${this.photoEditingJobId}`;
      }
    });
  }

  @action
  updatePhotoEditingJobEstimatedTime() {
    return Api.post(`/photo-editing/jobs/${this.photoEditingJobId}/update-estimated-time`).then(response => {
      this.photoEditingJob = new PhotoEditingJob(response.data.photoEditingJob);
    });
  }

  @action
  getPhotoEditingImageProduct(typeId, productId) {
    return Api.get(`/products/${productId}`).then(response => {
      this.photoEditingImageProducts[typeId] = new Product(response.data.product);
    });
  }

  @action
  toggleTimeOfDay(photoEditingImageId) {
    this.photoEditingJob.photoEditingImages
      .find(photoEditingImage => photoEditingImage.photoEditingImageId === photoEditingImageId)
      .toggleTimeOfDay();

    return this.saveImage(photoEditingImageId);
  }

  @action
  toggleLocation(photoEditingImageId) {
    this.photoEditingJob.photoEditingImages
      .find(photoEditingImage => photoEditingImage.photoEditingImageId === photoEditingImageId)
      .toggleLocation();

    return this.saveImage(photoEditingImageId);
  }

  @action
  removeImage(photoEditingImageId) {
    this.isLoading = true;

    return Api.delete(`/photo-editing/images/${photoEditingImageId}`).then(() => {
      const image = this.photoEditingJob.photoEditingImages.find(
        photoEditingImage => photoEditingImage.photoEditingImageId === photoEditingImageId,
      );

      const imageAtIndex = this.photoEditingJob.photoEditingImages.indexOf(image);

      this.photoEditingJob.photoEditingImages.splice(imageAtIndex, 1);
      this.isLoading = false;
    });
  }

  @action
  saveImage(photoEditingImageId) {
    // We purposely don't check if isSaving is set to true; we'll allow multiple calls to this function
    // even if there's one already on Api call active since this function is debounced.

    // Note that if different photos are being saved almost at the same time, the one that finishes first
    // will potentially set isSavingImage to false even if there's still another xhr in progress.
    // Since that scenario has low changes to happen we won't handle it.
    this.isSavingImage = true;

    const photoEditingImage = this.photoEditingJob.photoEditingImages.find(
      image => image.photoEditingImageId === photoEditingImageId,
    );

    return Api.post(`/photo-editing/images/${photoEditingImageId}`, {
      timeOfDay: photoEditingImage.timeOfDay,
      location: photoEditingImage.location,
    }).finally(() => {
      this.isSavingImage = false;
    });
  }

  @action
  onLabelChange(label) {
    this.label = label;
  }

  @action
  addPhotoEditingLabel() {
    if (this.label.trim() === "") {
      return Promise.resolve();
    }

    if (this.isSavingLabel) {
      return Promise.resolve();
    }

    this.isSavingLabel = true;

    return Api.post("/photo-editing/labels", {
      photoEditingJobId: this.photoEditingJobId,
      name: this.label,
    }).then(response => {
      this.label = "";
      this.isSavingLabel = false;
      this.photoEditingJob.addPhotoEditingLabel(response.data.photoEditingLabel);
    });
  }

  @action
  deletePhotoEditingLabel(photoEditingLabelId) {
    // if label doesn't exists, don't even attempt to send an xhr.
    if (!this.photoEditingJob.photoEditingLabels.find(label => label.photoEditingLabelId === photoEditingLabelId)) {
      return Promise.resolve();
    }

    // first delete the label from the collection, we don't want to show a spinner nor block the screen (loader)
    // when a label is deleted.
    this.photoEditingJob.removePhotoEditingLabelById(photoEditingLabelId);

    return Api.delete(`/photo-editing/labels/${photoEditingLabelId}`);
  }

  @action
  payAndProcess() {
    if (this.isLoading) {
      return Promise.resolve();
    }

    if (this.isSavingImage) {
      return Promise.resolve();
    }

    if (this.isSavingLabel) {
      return Promise.resolve();
    }

    this.isLoading = true;

    return Api.post(`/photo-editing/jobs/${this.photoEditingJobId}/start-processing`)
      .then(() => {
        // path relative to base path defined in Routes.jsx
        this.redirectTo = "/";
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  @action
  batchUpdateImageSettings(settings) {
    this.isSavingImage = true;

    return Api.post(`/photo-editing/jobs/${this.photoEditingJobId}/batch-update-image-settings`, settings)
      .then(response => {
        const { photoEditingImages = [] } = response.data.photoEditingJob;

        photoEditingImages.forEach(photoEditingImage => {
          const foundIndex = findIndex(this.photoEditingJob.photoEditingImages, [
            "photoEditingImageId",
            photoEditingImage.photoEditingImageId,
          ]);

          this.photoEditingJob.photoEditingImages[foundIndex].timeOfDay = photoEditingImage.timeOfDay;
          this.photoEditingJob.photoEditingImages[foundIndex].location = photoEditingImage.location;
        });
      })
      .finally(() => {
        this.isSavingImage = false;
      });
  }

  @computed
  get costPerItem() {
    return this.photoEditingImageProduct.discountedCreditCost;
  }

  @computed
  get totalCost() {
    const numberOfProducts =
      this.photoEditingJob.typeId === JobType.HD_REAL
        ? Math.ceil(this.photoEditingJob.numberOfProducts / HdRealImagesPerProduct)
        : this.photoEditingJob.numberOfProducts;

    return this.costPerItem * (numberOfProducts - this.photoEditingJob.numberOfFreeProducts);
  }
}

export default ReviewJobStore;
